/**
 * Corner rounding parameters for polygon vertices.
 * Ported from androidx.graphics.shapes.CornerRounding.kt
 *
 * @see https://m3.material.io/styles/shape/corner-radius-scale
 */

/**
 * Defines how a polygon corner is rounded.
 *
 * @example
 * // Fully rounded corner with radius 16
 * const rounding: CornerRounding = { radius: 16, smoothing: 0 };
 *
 * @example
 * // Rounded corner with iOS-style squircle smoothing
 * const smoothRounding: CornerRounding = { radius: 16, smoothing: 0.6 };
 */
export interface CornerRounding {
	/**
	 * The radius of the rounding. A value of 0 means no rounding (sharp corner).
	 * @default 0
	 */
	readonly radius: number;

	/**
	 * Smoothing factor in [0, 1]. Controls how the rounding curve transitions from
	 * the straight edge to the circular arc (squircle-like effect).
	 * 0 = pure circular arc, 1 = maximum smoothing.
	 * @default 0
	 */
	readonly smoothing: number;
}

/**
 * Creates a CornerRounding.
 * @param radius - Rounding radius (default: 0)
 * @param smoothing - Smoothing factor 0–1 (default: 0)
 */
export function cornerRounding(radius = 0, smoothing = 0): CornerRounding {
	return { radius, smoothing };
}

/** No rounding: straight/sharp corners. */
export const UNROUNDED: CornerRounding = Object.freeze({
	radius: 0,
	smoothing: 0,
});
